import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EVENT_TYPE_EMOJI } from "@/lib/games";
import { Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Corporate", "School", "College", "Birthday", "Wedding", "Family", "Conference", "Exhibition", "Festival", "Competition", "Other"];
const THEMES = ["#FF3B30", "#007AFF", "#34C759", "#FFCC00", "#AF52DE", "#FF2D55"];

export default function CreateArena() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", event_type: "Festival", custom_event_type: "", date: "", start_time: "", end_time: "",
    location: "", organizer_name: "", theme: "#FF3B30", cover_image_url: "", template: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Give your arena a name"); return; }
    setSaving(true);
    try {
      const payload = { ...f, event_type: f.event_type === "Other" && f.custom_event_type ? f.custom_event_type : f.event_type };
      const res = await api.post("/events", payload);
      toast.success("Your Arena is ready! 🎯");
      navigate(`/arena/${res.data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create arena");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-10">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground mb-6 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-2 mb-2"><Sparkles className="w-6 h-6 text-primary" /><h1 className="font-head font-black text-3xl">Create Your Arena</h1></div>
      <p className="text-muted-foreground mb-8">Fill the basics — we'll generate the player, game master and big-screen experiences instantly.</p>

      <div className="brut p-6 space-y-5">
        <div>
          <Label className="mb-1.5 block">Arena / Event name *</Label>
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. School Fun Day 2026" data-testid="arena-name" />
        </div>

        <div>
          <Label className="mb-1.5 block">Event type</Label>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => set("event_type", t)} data-testid={`type-${t}`}
                className={`px-3.5 py-2 rounded-full border-2 border-neutral-300 text-sm font-semibold lift ${f.event_type === t ? "bg-primary text-white" : "bg-card"}`}>
                <span className="mr-1">{EVENT_TYPE_EMOJI[t]}</span>{t}
              </button>
            ))}
          </div>
          {f.event_type === "Other" && (
            <Input className="mt-3" value={f.custom_event_type} onChange={(e) => set("custom_event_type", e.target.value)} placeholder="Custom event type" data-testid="custom-type" />
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="arena-date" /></div>
          <div><Label className="mb-1.5 block">Location</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Venue" data-testid="arena-location" /></div>
          <div><Label className="mb-1.5 block">Start time</Label><Input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} /></div>
          <div><Label className="mb-1.5 block">End time</Label><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} /></div>
        </div>

        <div><Label className="mb-1.5 block">Organizer name</Label><Input value={f.organizer_name} onChange={(e) => set("organizer_name", e.target.value)} placeholder="Who's running this?" /></div>
        <div><Label className="mb-1.5 block">Cover image URL (optional)</Label><Input value={f.cover_image_url} onChange={(e) => set("cover_image_url", e.target.value)} placeholder="https://…" /></div>

        <div>
          <Label className="mb-1.5 block">Theme colour</Label>
          <div className="flex gap-3">
            {THEMES.map((c) => (
              <button key={c} onClick={() => set("theme", c)} style={{ background: c }}
                className={`w-9 h-9 rounded-full border-2 ${f.theme === c ? "ring-2 ring-offset-2 ring-offset-background ring-white border-white" : "border-neutral-300"}`} data-testid={`theme-${c}`} />
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-1.5 block">Start from a template (optional)</Label>
          <Select value={f.template} onValueChange={(v) => set("template", v)}>
            <SelectTrigger data-testid="template-select"><SelectValue placeholder="Blank arena" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Corporate">Corporate Fun Day</SelectItem>
              <SelectItem value="School">School Annual Day</SelectItem>
              <SelectItem value="Birthday">Birthday Party</SelectItem>
              <SelectItem value="Wedding">Wedding</SelectItem>
              <SelectItem value="Exhibition">Exhibition</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Templates pre-fill Challenge Zones so you're live even faster.</p>
        </div>

        <Button className="w-full rounded-full font-bold text-base h-12 brut-hard" onClick={submit} disabled={saving} data-testid="create-arena-submit">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Arena 🚀"}
        </Button>
      </div>
    </div>
  );
}
